import React from 'react';
import AdminTableHeader from '../../../../components/Admin/AdminTableHeader';
import { NoRecordFound } from '../../../../components/Common/NoRecordFound';
import { LogModel } from '../../../../interfaces/log.interface';
import { convertTimestampToDateTime } from '../../../../helper/helper';

interface ErrorLogsTableProps {
  errorLogs: LogModel[];
}

const MakeFormatString = (str: string) =>
  str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const TABLE_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'severity', label: 'Severity' },
  { key: 'module', label: 'Module' },
  { key: 'errorCode', label: 'Error Code' },
  { key: 'errorProvider', label: 'Provider' },
  { key: 'errorSource', label: 'Source' },
  { key: 'failedPrompt', label: 'Original / Failed Prompt', minWidth: '20rem' },
  { key: 'softenedPrompt', label: 'Softened Prompt', minWidth: '16rem' },
  { key: 'error', label: 'Error / Stack', minWidth: '16rem' },
  { key: 'job_id', label: 'Job Id' },
  { key: 'status', label: 'Status' },
  { key: 'user', label: 'User' },
  { key: 'book', label: 'Book' },
];

const ErrorLogsTable: React.FC<ErrorLogsTableProps> = ({ errorLogs }) => (
  <table className="min-w-full border border-gray-300">
    <AdminTableHeader columns={TABLE_COLUMNS} />
    <tbody>
      {errorLogs.length > 0 ? (
        errorLogs.map((log, index) => {
          // Treat records without an explicit severity as 'error' so legacy
          // rows render the same as new error rows.
          const severity = log.severity || 'error';
          const isWarning = severity === 'warning';
          return (
            <tr key={log.id || index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
              <td className="text-left py-2 px-4 border-b text-xs whitespace-nowrap text-gray-500">
                {convertTimestampToDateTime(log.timestamp)}
              </td>
              <td className="text-left py-2 px-4 border-b">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  isWarning ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'
                }`}>
                  {isWarning ? 'Warning' : 'Error'}
                </span>
              </td>
              <td className="text-left py-2 px-4 border-b text-xs">
                {MakeFormatString(log.module)}
              </td>
              <td className="text-left py-2 px-4 border-b">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  log.errorCode
                    ? (isWarning ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700')
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {log.errorCode ? MakeFormatString(log.errorCode) : '—'}
                </span>
              </td>
              <td className="text-left py-2 px-4 border-b">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  log.errorProvider ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {log.errorProvider || '—'}
                </span>
              </td>
              <td className="text-left py-2 px-4 border-b text-xs text-gray-500">
                {log.errorSource || '—'}
              </td>
              <td className="text-left py-2 px-4 border-b text-xs" style={{ minWidth: '20rem', maxWidth: '24rem' }}>
                {log.failedPrompt ? (
                  <div className="break-words whitespace-pre-wrap text-gray-700 font-mono text-[0.7rem]">{log.failedPrompt}</div>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="text-left py-2 px-4 border-b text-xs" style={{ minWidth: '16rem', maxWidth: '20rem' }}>
                {log.softenedPrompt ? (
                  <div className="break-words whitespace-pre-wrap text-amber-900 font-mono text-[0.7rem]">{log.softenedPrompt}</div>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="text-left py-2 px-4 border-b" style={{ minWidth: '16rem', maxWidth: '20rem' }}>
                {log.error && (
                  <div className="text-xs font-medium text-gray-900 mb-1 break-words">{log.error}</div>
                )}
                {log.errorStack && (() => {
                  try {
                    const obj = JSON.parse(log.errorStack);
                    return (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-400 hover:text-gray-700">stack</summary>
                        <pre className="whitespace-pre-wrap break-words font-mono mt-1">{JSON.stringify(obj, null, 2)}</pre>
                      </details>
                    );
                  } catch {
                    return (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-400 hover:text-gray-700">stack</summary>
                        <pre className="whitespace-pre-wrap break-words mt-1">{log.errorStack}</pre>
                      </details>
                    );
                  }
                })()}
              </td>
              <td className="text-left py-2 px-4 border-b text-xs font-mono text-gray-600">
                {log.job_id || '—'}
              </td>
              <td className="text-left py-2 px-4 border-b text-xs capitalize">{log.status}</td>
              <td className="text-left py-2 px-4 border-b text-xs">{log.user || '—'}</td>
              <td className="text-left py-2 px-4 border-b text-xs">
                <div className="capitalize">{log.bookTitle || '—'}</div>
                <div className="font-mono text-gray-400">{log.bookId || ''}</div>
                {log.pageKey && <div className="text-gray-400">pg: {log.pageKey}</div>}
              </td>
            </tr>
          );
        })
      ) : (
        <NoRecordFound colSpan={TABLE_COLUMNS.length} />
      )}
    </tbody>
  </table>
);

export default ErrorLogsTable;
